<!--reusable component for table
component imported as Table in many files
-->
<template>
  <v-col class="px-0 py-0">
    <v-row
      no-gutters
      class="align-items-end mb-2 min-height-36"
      v-if="
        showPageSizeSelector ||
          $slots['table-top-right'] ||
          $slots['table-top-full']
      "
    >
      <v-col align="start">
        <emailCount
          v-if="showPageSizeSelector"
          :customDataList="customDataList"
          :pageSizeOption="pageSizeOption"
          @recordsSelected="triggerUpdateTableSize"
          :defaultSelected="parseInt(paginationData.pageSize, 10)"
          :dataTestId="dataTestId"
        />
      </v-col>
      <v-col v-if="$slots['table-top-full']">
        <slot name="table-top-full"></slot
      ></v-col>
      <v-col
        align="end"
        v-if="$slots['table-top-right']"
        class="table-top-right"
      >
        <slot name="table-top-right"></slot>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col sm="12">
        <div class="actionTables">
          <table class="vgt-table" cellspacing="0" :data-test-id="dataTestId">
            <thead>
              <tr>
                <th
                  class="text-left"
                  :class="{ 'header-fixed': headerFixed }"
                  v-if="showSelectAll"
                  width="5%"
                >
                  <Checkbox
                    theme="dark"
                    id="ibth"
                    v-model="selectAll"
                    :data-test-id="`${dataTestId}__select-all-checkbox`"
                  />
                </th>
                <th
                  class="text-left"
                  v-for="(item, index) in columns"
                  :class="{
                    'header-fixed': headerFixed
                  }"
                  :key="index"
                  :width="getColWidth(item, columns)"
                >
                  <span
                    :data-test-id="`${dataTestId}__header__${item.field}`"
                    @click.stop="sortData(item)"
                    :class="{
                      sortable: item.sortable
                    }"
                  >
                    {{ item.label }}
                    <template v-if="item.sortable">
                      <v-icon
                        class="ml-1 icon-top4"
                        :color="item.sortOrder ? '#58e2c2' : 'white'"
                      >
                        {{
                          item.sortOrder === 'ASC'
                            ? 'expand_less'
                            : 'expand_more'
                        }}
                      </v-icon>
                    </template>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <slot
                name="table_item"
                :tableRows="tableRows"
                :selectedItems="selectedItems"
              ></slot>
              <tr v-if="tableRows.length === 0">
                <td v-if="loading" colspan="12" class="text-center">
                  <TableLoader
                    :loading="loading"
                    :data-test-id="`${dataTestId}__loader`"
                  />
                </td>
                <td
                  v-if="!loading"
                  colspan="12"
                  class="text-center"
                  :data-test-id="`${dataTestId}__no-records-text`"
                >
                  No records
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <v-row no-gutters class="align-items-center">
          <v-col sm="7" v-if="showCurrentShowing">
            <label class="bold results" v-if="currentPageIndex">
              Showing {{ currentPageIndex.from }} - {{ currentPageIndex.to }} of
              {{ numberINString(paginationData.totalRecords) }} records
            </label>
          </v-col>
          <v-col sm="5">
            <Pagination
              v-if="
                showPagination &&
                  paginationData &&
                  paginationData.totalRecords >= paginationData.pageSize
              "
              :data-test-id="`${dataTestId}__pagination`"
              :pageNumber="paginationData.pageNumber"
              :totalRecords="paginationData.totalRecords"
              :recordsPerPage="paginationData.pageSize"
              @pageChanged="paginationData.setPage($event)"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-col>
</template>
<script>
import Pagination from '@/components/Discovery/Widgets/Pagination';
import TableLoader from '@/components/Common/TableLoader';
import Checkbox from '@/components/Common/Checkbox';
import EmailCount from '@/components/Common/TableRecordDisplaySelector';
import { numberINString } from '@/helpers/common';

import { tableSort } from '@/helpers/TableHelpers';
export default {
  components: { Pagination, TableLoader, Checkbox, EmailCount },
  props: {
    label: {
      type: String,
      default: 'table',
      required: false
    },
    customDataList: Array,
    sortBy: {
      required: false,
      type: Function
    },
    pageSizeOption: { type: String, default: 'OPTION_1' },
    showResults: {
      required: false,
      default: true,
      type: Boolean
    },
    showPageSizeSelector: {
      required: false,
      default: true,
      type: Boolean
    },
    showCurrentShowing: {
      required: false,
      default: true,
      type: Boolean
    },
    tableRows: Array,
    updateTableSize: Function,
    paginationData: {
      type: Object,
      default: function() {
        return {
          pageSize: null,
          pageNumber: 1,
          totalRecords: this.tableRows.length,
          setPage: this.onPageChange
        };
      }
    },
    showSelectAll: Boolean,
    selectedItems: Array,

    selectAllItems: Function,
    showPagination: {
      type: Boolean,
      default: true
    },
    loading: Boolean,
    headerFixed: Boolean,
    columns: Array,
    changeCheckState: Boolean,
    dataTestId: String
  },

  data() {
    return {
      numberINString,
      currentPageIndex: null,
      selectAll: false
    };
  },

  methods: {
    /**
     * To update table page size
     * @param {Number} pageSize
     */
    triggerUpdateTableSize(pageSize) {
      this.selectAll = false;
      this.updateTableSize(pageSize);
    },
    /**
     * to change table page
     * @param {Number} pageNumber
     */
    onPageChange(pageNumber) {
      this.pageNumber = pageNumber;
      if (this.getCurrentPage() !== this.pageNumber) {
        this.$router.push('p' + this.pageNumber);
      }
    },
    /**
     * set page size to new page size
     * @param {Number} newPageSize
     */
    onPageSizeChange(newPageSize) {
      this.$store.state[this.label] = newPageSize;
      this.pageNumber = this.getCurrentPage();
    },
    // return current page
    getCurrentPage() {
      return (
        (this.$route.params && parseInt(this.$route.params.pageNumber, 10)) || 1
      );
    },
    /**
     * sorts table data with respect to particular column
     * @param {Object} item
     */
    sortData(item) {
      if (item.sortable) {
        const tableSortData = tableSort({ columns: this.columns, item });
        this.sortBy(tableSortData);
      }
    },
    /**
     * To get table column width
     * Case 1 :- all cols without width, then all cols width will be eqaul
     * Case 2 :- some cols without width, then cols without width will be '15%'
     * Case 3 :- all cols have width, then return width as it is
     * @param {Object} column, consist label and width
     * @param {Array} columns
     *
     * @return width
     */
    getColWidth({ width, label }, columns) {
      const allColsWithoutWidth = (columns || []).every(
        column => !column.width
      );
      if (allColsWithoutWidth) {
        const noOfCols = (columns || []).length;
        const total = 100;
        return `${total / noOfCols}%`;
      }
      const someColsWithoutWidth = (columns || []).some(
        column => !column.width
      );
      if (someColsWithoutWidth) {
        const labelFirstWord = (label || '').split(' ')?.[0];
        const maxLength = 7;
        return width || (labelFirstWord.length < maxLength ? '15%' : '25%');
      }
      return width;
    }
  },
  watch: {
    tableRows: function() {
      if (this.showResults) {
        this.currentPageIndex = this.getPageRecordFromToIndex(
          this.paginationData.pageNumber,
          this.paginationData.pageSize,
          this.tableRows.length
        );
      }
    },
    selectAll: function() {
      this.selectAllItems(this.selectAll);
    },
    changeCheckState(newVal) {
      if (typeof newVal === 'boolean' && !newVal) {
        this.selectAll = false;
      }
    }
  }
};
</script>
<style>
/* background-color for even */
.actionTables .vgt-table thead th {
  background-color: #ffffff;
}
.actionTables .vgt-table tbody tr:nth-child(even) {
  background-color: #f4f4f5;
}
.actionTables .vgt-table {
  overflow-x: scroll;
}
@media (max-width: 770px) {
  .actionTables .vgt-table {
    display: block;
  }
}
/* wrap text  */
.wrap-text {
  word-break: break-all;
}

/* background-color for even checkbox */
.actionTables .vgt-table tbody tr:nth-child(even) .checkmark {
  background-color: #ffffff;
}

.header-fixed {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
}

/* results */
.results {
  color: #58e2c2;
  font-weight: bold;
  font-size: 12px;
}
.v-input .v-input__control .v-input__slot {
  margin-bottom: 0px !important;
}

.min-height-36 {
  min-height: 36px;
}

td {
  font-size: 12px;
  font-weight: 600;
}

.align-items-center {
  align-items: center;
}
.align-items-end {
  align-items: flex-end;
}

/* for clickable */
.clickable {
  cursor: pointer;
  font-weight: 800 !important;
}
.table-top-right {
  text-align: -webkit-right;
}
</style>
